import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, FormControl, FormHelperText, MenuItem, TextField, Typography, Link } from '@mui/material'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { styled } from '@mui/material/styles'
import { CHECK_STATUS } from 'api/checkresponse'
import { UPLOAD_PATH_TYPE } from 'api/uploadpath'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import { selectStartdelivery } from 'slices/startdeliverySlice'
import { checkresponse, selectCheckresponse } from 'slices/checkresponseSlice'
import { driverSend, selectDriverSend } from 'slices/driverSendSlice'
import { PrivacyPolicyDialog } from 'components/DriverInput/PrivacyPolicyDialog'

const MESSAGE_MAX_LENGTH = 100
const DRIVER_FACE = 'driver-face'
const DELIVERY_SLIP = 'delivery-slip'

const driverOptions = ['Amazon', 'ヤマト運輸', '日本郵便', '佐川急便', 'Uber Eats', '出前館', 'Menu', 'その他']

const LabelComponent: FC<{ required?: boolean; error?: boolean }> = ({ children, required = false, error = false }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, mb: 1 }}>
      <Typography sx={{ color: error ? 'error.main' : '', fontWeight: 'bold' }}>{children}</Typography>
      {required && (
        <Typography variant="caption" sx={{ color: 'red', px: 1 }}>
          ※必須
        </Typography>
      )}
    </Box>
  )
}

const InputCamera = styled('input')({
  display: 'none',
})

export const DriverInput = () => {
  const dispatch = useAppDispatch()
  const { sessionId } = useAppSelector(selectStartdelivery)
  const { status } = useAppSelector(selectDriverSend)
  const { response } = useAppSelector(selectCheckresponse)

  const [deliveryCompanyName, setDeliveryCompanyName] = useState('')
  const [parcelFile, setParcelFile] = useState<FileList>()
  const [slipFile, setSlipFile] = useState<FileList>()
  const [message, setMessage] = useState('')
  const [open, setOpen] = useState(false)

  const navigate = useNavigate()
  useEffect(() => {
    if (status === 'success') {
      dispatch(checkresponse(sessionId))
    }
  }, [dispatch, sessionId, status])

  useEffect(() => {
    if (response?.status === CHECK_STATUS.RESPONDED) {
      navigate('/next', { replace: true })
    }
  }, [response?.status])

  const handleDriverChange = (event: SelectChangeEvent) => {
    setDeliveryCompanyName(event.target.value as string)
  }

  const imageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = event.target
    if (files && files.length) {
      name === DRIVER_FACE ? setParcelFile(files) : setSlipFile(files)
      const filereader = new FileReader()
      filereader.readAsDataURL(files[0])
      filereader.onload = () =>
        ((document.getElementById(`img-${name}`) as HTMLInputElement).src = filereader.result as string)
    }
  }

  const messageError = useMemo(() => MESSAGE_MAX_LENGTH < message.length, [message])
  const parcelFileSizeOver = useMemo(() => (parcelFile ? parcelFile[0].size >= 1024 * 1024 * 10 : false), [parcelFile])
  const slipFileSizeOver = useMemo(() => (slipFile ? slipFile[0].size >= 1024 * 1024 * 10 : false), [slipFile])

  const disabled = useMemo(
    () => !deliveryCompanyName || !parcelFile || messageError || parcelFileSizeOver || slipFileSizeOver,
    [deliveryCompanyName, parcelFile, messageError, parcelFileSizeOver, slipFileSizeOver]
  )

  const handleClick = useCallback(() => {
    const type = slipFile ? UPLOAD_PATH_TYPE.BOTH : UPLOAD_PATH_TYPE.PARCEL
    dispatch(driverSend({ sessionId, type, parcelFile, slipFile, data: { deliveryCompanyName, message } }))
  }, [dispatch, deliveryCompanyName, message, parcelFile, sessionId, slipFile])

  return (
    <>
      <Box sx={{ backgroundColor: 'grey.200', p: 2 }}>
        <Typography textAlign="center" sx={{ mb: 1 }}>
          配達情報を入力してください
        </Typography>
        <Typography variant="body2" textAlign="center" sx={{ color: 'grey.700' }}>
          入力完了後に「プライバシーポリシーに同意し連絡する」をタップすると居住者に情報を送信できます
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <FormControl fullWidth>
          <LabelComponent required>業者選択</LabelComponent>

          <Select displayEmpty value={deliveryCompanyName} onChange={handleDriverChange}>
            <MenuItem sx={{ display: 'none', color: 'grey.500' }} value="">
              選択してください
            </MenuItem>
            {driverOptions.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>ご自身の業者名を選択してください</FormHelperText>
        </FormControl>

        <LabelComponent required>ご自身の顔写真(10MBまで)</LabelComponent>
        <Typography variant="body2">居住者に連絡を取るため、顔写真を撮影してください</Typography>
        <Typography variant="caption" sx={{ color: 'error.main' }}>
          {parcelFileSizeOver ? '画像サイズが10MBを超えています' : ''}
        </Typography>
        <label htmlFor={DRIVER_FACE}>
          <InputCamera
            type="file"
            id={DRIVER_FACE}
            name={DRIVER_FACE}
            accept="image/*"
            capture="user"
            onChange={imageUpload}
          />
          <Button component="span" variant="outlined" size="large" fullWidth sx={{ my: 2 }}>
            ご自身の顔を撮影
          </Button>
        </label>
        <Box component="img" id={`img-${DRIVER_FACE}`} sx={{ maxWidth: '100%' }}></Box>

        <LabelComponent>荷物／伝票画像(10MBまで)</LabelComponent>
        <Typography variant="caption" sx={{ color: 'error.main' }}>
          {slipFileSizeOver ? '画像サイズが10MBを超えています' : ''}
        </Typography>
        <label htmlFor={DELIVERY_SLIP}>
          <InputCamera
            type="file"
            id={DELIVERY_SLIP}
            name={DELIVERY_SLIP}
            accept="image/*"
            capture="environment"
            onChange={imageUpload}
          />
          <Button component="span" variant="outlined" size="large" fullWidth sx={{ my: 2 }}>
            伝票画像を撮影
          </Button>
        </label>
        <Box component="img" id={`img-${DELIVERY_SLIP}`} sx={{ maxWidth: '100%' }}></Box>

        <LabelComponent error={messageError}>居住者へのメッセージ</LabelComponent>
        <TextField
          fullWidth
          multiline
          error={messageError}
          rows={4}
          helperText={messageError ? '100文字以下で入力してください' : '必要な場合のみ入力ください(100文字以内)'}
          value={message}
          onChange={event => setMessage(event.target.value)}
        ></TextField>

        <Button variant="contained" size="large" disabled={disabled} fullWidth sx={{ mt: 3 }} onClick={handleClick}>
          プライバシーポリシーに同意し連絡する
        </Button>

        <Box sx={{ typography: 'caption', textAlign: 'center', my: 1 }}>
          <Link component="button" onClick={() => setOpen(true)}>
            プライバシーポリシー
          </Link>
        </Box>
      </Box>
      <PrivacyPolicyDialog open={open} onClose={() => setOpen(false)} />
    </>
  )
}
